// Harshada Bhavsar (21/07/2025)
// Employee Form and api
import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import bgImage from "../../../../Assets/bg3.jpg";
import { createEmployee } from "../Services/AddEmployeeService";
import { GetEmployeeName } from "../../../Auth/Services/authService";
import {
  getRoles,
  getFunctions,
  getDepartments,
} from "../../../Auth/Services/metadataService";
import { useNavigate } from "react-router-dom";
import {
  getEmployeeSuggestions,
  getGrades,
  getZones,
  getJobLocations,
} from "../../../Auth/Services/metadataService";
import BulkImportEmployeeModal from "../Modal/BulkImportEmployeeModal";
import NormalAlert from "../../../Alerts/NormalAlert";

const AddEmployee = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    employeeCode: "",
    employeeName: "",
    email: "",
    phoneNumber: "",
    department: {
      id: "",
      name: "",
    },
    function: {
      id: "",
      name: "",
    },
    role: 0,
    designation: "",
    reportingManagerCode: "",
    reportingManagerName: "",
    reportingManagerDisplay: "",
    grade: { id: "", name: "" },
    zone: { id: "", name: "" },
    location: { id: "", name: "" },
  });

  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [functions, setFunctions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [grades, setGrades] = useState([]);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "reportingManagerCode") {
      const value = e.target.value;

      setFormData((prev) => ({
        ...prev,
        reportingManagerDisplay: value,
        reportingManagerCode: "",
        reportingManagerName: "",
      }));

      if (value.trim().length >= 3) {
        const res = await getEmployeeSuggestions(value.trim());

        if (res?.length > 0) {
          setSuggestions(res);
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0) {
        const s = suggestions[highlightedIndex];
        setFormData({
          ...formData,
          reportingManagerCode: s.employeeCode,
          reportingManagerName: s.employeeName,
        });
        setShowSuggestions(false);
      }
    }
  };

  useEffect(() => {
    const loadDropdownData = async () => {
      const fun = await getFunctions();
      setFunctions(fun);

      const roleData = await getRoles("Employee");
      setRoles(roleData);
    };

    loadDropdownData();
  }, []);

  useEffect(() => {
    if (!formData.function.id) {
      setDepartments([]);
      return;
    }

    const fetchDepartments = async () => {
      const dept = await getDepartments(formData.function.id);
      setDepartments(dept || []);
    };

    fetchDepartments();
  }, [formData.function.id]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employeeCode.trim())
      newErrors.employeeCode = "Employee Code is required";
    if (!formData.employeeName.trim())
      newErrors.employeeName = "Employee Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone Number is required";
    if (!formData.function.id) newErrors.functionID = "Function is required";
    if (!formData.department?.id)
      newErrors.department = "Department is required";
    if (!formData.designation.trim())
      newErrors.designation = "Designation is required";
    if (!formData.role || formData.role === 0)
      newErrors.role = "Role is required";
    if (!formData.grade.id) newErrors.grade = "Grade is required";
    if (!formData.zone.id) newErrors.zone = "Zone is required";
    if (!formData.location.id) newErrors.location = "Location is required";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage("");
    setError("");

    const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    try {
      const token = Cookies.get("employeeToken");

      const payload = {
        token,

        employeeCode: formData.employeeCode,
        employeeName: formData.employeeName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,

        managerEmpCode: formData.reportingManagerCode,

        designation: formData.designation,

        functions: formData.function.name,
        department: formData.department?.name || "",
        grade: formData.grade.name,
        zone: formData.zone.name,
        location: formData.location.name,

        role: formData.role,
      };

      const response = await createEmployee(payload);
      const data = response.data;

      if (data.status === "Success") {
        setMessage(data.message || "Employee added successfully.");
        setFormData({
          employeeCode: "",
          employeeName: "",
          email: "",
          phoneNumber: "",
          department: { id: "", name: "" },
          function: {
            id: "",
            name: "",
          },
          role: 0,
          designation: "",
          reportingManagerCode: "",
          reportingManagerName: "",
          reportingManagerDisplay: "",
          grade: { id: "", name: "" },
          zone: { id: "", name: "" },
          location: { id: "", name: "" },
        });
      } else {
        if (data.errors) {
          setErrors(data.errors);
        } else {
          setError(data.message || "Something went wrong.");
        }
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.errors) {
        setErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || "Something went wrong.");
      }
    }
  };

  const handleReset = () => {
    setFormData({
      employeeCode: "",
      employeeName: "",
      email: "",
      phoneNumber: "",
      department: { id: "", name: "" },
      function: {
        id: "",
        name: "",
      },
      role: 0,
      designation: "",
      reportingManagerCode: "",
      reportingManagerName: "",
      reportingManagerDisplay: "",
      grade: { id: "", name: "" },
      zone: { id: "", name: "" },
      location: { id: "", name: "" },
    });

    setDepartments([]);
    setErrors({});
    setMessage("");
    setError("");
  };

  useEffect(() => {
    const checkRole = async () => {
      const token = Cookies.get("employeeToken");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        const userRole = response?.data?.employeeRole;

        if (userRole !== "Admin Employee") {
          navigate("/Dashboard");
        }
      } catch (err) {
        navigate("/Dashboard");
      }
    };

    checkRole();
  }, []);

  useEffect(() => {
    const loadDropdownData = async () => {
      const fun = await getFunctions();
      setFunctions(fun);

      const roleData = await getRoles("Employee");
      setRoles(roleData);

      const gradeData = await getGrades();
      setGrades(gradeData);

      const zoneData = await getZones();
      setZones(zoneData);
    };

    loadDropdownData();
  }, []);

  useEffect(() => {
    if (formData.zone.id) {
      getJobLocations(formData.zone.id).then(setLocations);
    } else {
      setLocations([]);
    }
  }, [formData.zone.id]);

  return (
    <>
    <div className="min-h-screen bg-[#f4f6f9]">
      {/* Top Banner */}
      <div
        className="w-full h-[200px] bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      />

      {/* Form Wrapper */}
      <div className="flex justify-center -mt-20 pb-20 px-4">
          <div className="w-full max-w-[52rem] bg-white rounded-lg shadow-lg p-8 relative">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <svg
                  width="70"
                  height="70"
                  viewBox="0 0 24 24"
                  fill="#0467c5"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Head */}
                  <circle cx="12" cy="8" r="4" />

                  {/* Body */}
                  <path d="M4 20c0-3.5 3.5-6 8-6s8 2.5 8 6v1H4v-1z" />
                </svg>
              </div>
              <h2 className="text-2xl font-medium text-[#0467c5]">
                Add Employee
              </h2>

              <button
                onClick={() => setShowModal(true)}
                className="absolute right-8 top-8 bg-primary text-white px-4 py-2 rounded-md text-sm"
              >
                Bulk Import
              </button>
            </div>

          <hr className="mb-8 border-gray-300" />

          {message && (
            <div className="mb-4 text-center bg-green-100 text-green-700 px-4 py-2 rounded-md">
              {message}
            </div>
          )}

          {error && (
            <div className="mb-4 text-center bg-red-100 text-red-700 px-4 py-2 rounded-md">
              {error}
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5"
          >
            {/* Employee Code */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Employee Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="employeeCode"
                value={formData.employeeCode}
                onChange={handleChange}
                placeholder="Employee Code"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {errors.employeeCode && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.employeeCode}
                </p>
              )}
            </div>

            {/* Employee Name */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Employee Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="employeeName"
                value={formData.employeeName}
                onChange={handleChange}
                placeholder="Employee Name"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {errors.employeeName && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.employeeName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                maxLength={10}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    handleChange(e);
                  }
                }}
                placeholder="Phone Number"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phoneNumber}
                </p>
              )}
            </div>

            <div className="relative">
              <label className="block mb-1 text-[14px] font-medium">
                Reporting Manager <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="reportingManagerCode"
                value={formData.reportingManagerDisplay}
                onChange={handleChange}
                placeholder="Reporting Manager"
                autoComplete="off"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute w-full bg-white border max-h-40 overflow-y-auto z-50">
                  {suggestions.map((s, index) => (
                    <li
                      key={index}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          reportingManagerCode: s.employeeCode,
                          reportingManagerName: s.employeeName,
                          reportingManagerDisplay: `${s.employeeCode} - ${s.employeeName}`,
                        });

                        setShowSuggestions(false);
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {s.employeeCode} - {s.employeeName}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Function */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Function <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.function.id}
                onChange={(e) => {
                  const selected = functions.find(
                    (f) => f.functionID == e.target.value,
                  );

                  setFormData((prev) => ({
                    ...prev,
                    function: {
                      id: Number(e.target.value),
                      name: selected?.functionName || "",
                    },
                  }));
                }}
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                <option value="">Select Function</option>
                {functions.map((fun) => (
                  <option key={fun.functionID} value={fun.functionID}>
                    {fun.functionName}
                  </option>
                ))}
              </select>
              {errors.functionID && (
                <p className="text-red-500 text-xs mt-1">{errors.functionID}</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                disabled={false}
                value={String(formData.department?.id || "")}
                onChange={(e) => {
                  const deptId = e.target.value;

                  const selectedDept = departments.find(
                    (d) => String(d.departmentID) === String(deptId),
                  );

                  setFormData((prev) => ({
                    ...prev,
                    department: {
                      id: selectedDept.departmentID,
                      name: selectedDept.departmentName,
                    },
                  }));
                }}
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                <option value="">Select Department</option>
                {departments.map((d) => (
                  <option key={d.departmentID} value={String(d.departmentID)}>
                    {d.departmentName}
                  </option>
                ))}
              </select>

              {errors.department && (
                <p className="text-red-500 text-xs mt-1">{errors.department}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: parseInt(e.target.value) })
                }
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                {errors.role && (
                  <p className="text-red-500 text-xs mt-1">{errors.role}</p>
                )}
                <option value={0}>Select Role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.role}
                  </option>
                ))}
              </select>

              {errors.role && (
                <p className="text-red-500 text-xs mt-1">{errors.role}</p>
              )}
            </div>

            {/* Designation */}
            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                placeholder="Designation"
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              />

              {errors.designation && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.designation}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Grade <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.grade.id}
                onChange={(e) => {
                  const selected = grades.find(
                    (g) => g.gradeID == e.target.value,
                  );

                  setFormData({
                    ...formData,
                    grade: {
                      id: e.target.value,
                      name: selected?.gradeName || "",
                    },
                  });
                }}
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                <option value="">Select Grade</option>
                {grades.map((g) => (
                  <option key={g.gradeID} value={g.gradeID}>
                    {g.gradeName}
                  </option>
                ))}
              </select>

              {errors.grade && (
                <p className="text-red-500 text-xs mt-1">{errors.grade}</p>
              )}
            </div>

            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Zone <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.zone.id}
                onChange={(e) => {
                  const selected = zones.find(
                    (z) => z.zoneID == e.target.value,
                  );

                  setFormData({
                    ...formData,
                    zone: {
                      id: e.target.value,
                      name: selected?.zoneName || "",
                    },
                    location: { id: "", name: "" }, // reset location
                  });
                }}
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                <option value="">Select Zone</option>
                {zones.map((z) => (
                  <option key={z.zoneID} value={z.zoneID}>
                    {z.zoneName}
                  </option>
                ))}
              </select>

              {errors.zone && (
                <p className="text-red-500 text-xs mt-1">{errors.zone}</p>
              )}
            </div>

            <div>
              <label className="block mb-1 text-[14px] font-medium">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.location.id}
                onChange={(e) => {
                  const selected = locations.find(
                    (l) => l.locationID == e.target.value,
                  );

                  setFormData({
                    ...formData,
                    location: {
                      id: e.target.value,
                      name: selected?.jobLocation || "",
                    },
                  });
                }}
                className="w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 border-blue-200 focus:ring-blue-300"
              >
                <option value="">Select Location</option>
                {locations.map((l) => (
                  <option key={l.locationID} value={l.locationID}>
                    {l.jobLocation}
                  </option>
                ))}
              </select>

              {errors.location && (
                <p className="text-red-500 text-xs mt-1">{errors.location}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="md:col-span-2 flex justify-center gap-6 mt-6">
              <button
                type="submit"
                className="bg-primary text-white px-8 py-2.5 text-sm rounded-md font-medium transition"
              >
                Submit
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="bg-gray-200 hover:bg-gray-300 px-8 py-2.5 text-sm rounded-md font-medium transition"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {showModal && (
            <BulkImportEmployeeModal
              show={showModal}
              onClose={() => setShowModal(false)}
              setAlert={setAlert}
            />
          )}

          {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
          </>
  );
};

export default AddEmployee;
